<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div class="historical-bid-chart">
    <chart
      v-if="chartOptions != null"
      template-ref="historicalBidChart.chart"
      :name="$t('messages.historicalAndForecastedBidCPM', { n: 1 })"
      :mode="chartMode.NONE"
      :echarts-options="chartOptions"
      :theme="chartTheme.HS_HISTORICAL_BIDS"
      autoresize />
  </div>
</template>

<script type="text/babel">
  import * as echarts from 'echarts';
  import { DateTime } from 'luxon';

  import Chart from '@/common/components/chart/Chart';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import { DASH } from '@/globals';
  import formatNumber from '@/utils/NumberFormatter';

  import bidData from './bid-data.json';

  export default {
    components: { Chart },
    props: {
      lineItem: {
        type: Object,
      },
      recommendedCPM: {
        type: Number,
        default: null,
      },
    },
    data() {
      return {
        bidData: [],
        chartMode: ChartMode,
        chartOptions: {},
        chartTheme: ChartTheme,
      };
    },
    computed: {},
    watch: {
      bidData(val) {
        this.$emit('bidDataChanged', val);
      },
      recommendedCPM() {
        if (this.updateChartOptionsTimer) {
          clearTimeout(this.updateChartOptionsTimer);
        }
        this.updateChartOptionsTimer = setTimeout(() => {
          this.updateChartOptionsTimer = null;
          this.updateChartOptions();
        }, 200);
      },
    },
    mounted() {
      // this.$refs.chart.dispatchAction({
      //   type: 'brush',
      //   areas: [
      //     {
      //       brushType: 'lineX',
      //       coordRange: ['2016-06-02', '2016-06-20'],
      //       xAxisIndex: 0,
      //     },
      //   ],
      // });
      this.updateChartOptions();
    },
    methods: {
      splitData(rawData) {
        const categoryData = [];
        const values = [];
        const volumes = [];
        for (let i = 0; i < rawData.length; i += 1) {
          categoryData.push(rawData[i].splice(0, 1)[0]);
          values.push(rawData[i]);
          volumes.push([i, rawData[i][4], rawData[i][0] > rawData[i][1] ? 1 : -1]);
        }

        return {
          categoryData,
          values,
          volumes,
        };
      },
      calculateMA(dayCount, data) {
        const result = [];
        for (let i = 0, len = data.values.length; i < len; i += 1) {
          if (i < dayCount) {
            result.push(DASH);
          } else {
            let sum = 0;
            for (let j = 0; j < dayCount; j += 1) {
              sum += data.values[i - j][1];
            }
            result.push(+(sum / dayCount).toFixed(3));
          }
        }
        return result;
      },
      updateChartOptions() {
        if (this.recommendedCPM == null) {
          return;
        }

        // const vGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
        //   offset: 0,
        //   color: '#FF0083',
        // }, {
        //   offset: 1,
        //   color: '#ff73c3',
        // }]);
        const vGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: '#c1dbff',
          },
          {
            offset: 1,
            color: '#d7e8ff',
          },
        ]);
        const vGradient2 = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: '#FF0083',
          },
          {
            offset: 1,
            color: '#ff0083cc',
          },
        ]);

        const startOfBidHistory =
          this.lineItem != null && this.lineItem.endDate != null
            ? DateTime.fromISO(this.lineItem.endDate).startOf('day').plus({ days: 1 }).minus({ hours: bidData.length })
            : DateTime.local().startOf('day').plus({ days: 1 }).minus({ hours: bidData.length });

        const ratio = bidData[0][1] / this.recommendedCPM / 0.6;
        const bidDataModified = bidData.map((x, i) => {
          const open = Math.round(100 * (x[1] / ratio)) / 100;
          const close = Math.round(100 * ((x[1] * (1.04 - Math.random() * 0.08)) / ratio)) / 100;
          const high = Math.max(open, close);
          const low = Math.min(open, close);

          return [
            DateTime.fromISO(startOfBidHistory).plus({ hours: i }).toLocaleString(DateTime.DATETIME_MED),
            open,
            close,
            Math.min(low, Math.round(100 * ((x[1] - x[1] * (Math.random() * 0.02 + 0.04)) / ratio)) / 100),
            Math.max(high, Math.round(100 * ((x[1] + x[1] * (Math.random() * 0.02 + 0.04)) / ratio)) / 100),
            Math.round(Math.random() * 500 + 1000),
          ];
        });
        const data = this.splitData(bidDataModified);
        this.bidData = data;
        this.chartOptions = {
          backgroundColor: '#fff',
          animation: false,
          legend: {
            bottom: 10,
            left: 'center',
            data: ['Bid range', 'Max', 'Avg', 'Min'],
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross',
            },
            backgroundColor: 'rgba(245, 245, 245, 0.8)',
            borderWidth: 1,
            borderColor: '#ccc',
            padding: 10,
            textStyle: {
              color: '#000',
            },
            position: (pos, params, el, elRect, size) => {
              const obj = { top: 10 };
              obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
              return obj;
            },
            // extraCssText: 'width: 170px'
            formatter: (p) => {
              const date = p[0].name;
              const seriesBidRange = p.find((x) => x.seriesName === 'Bid range');
              const seriesAvg = p.find((x) => x.seriesName === 'Avg');
              const seriesMin = p.find((x) => x.seriesName === 'Min');
              const seriesMax = p.find((x) => x.seriesName === 'Max');
              const seriesVolume = p.find((x) => x.seriesName === 'Bids');
              let upperQuartile = 0;
              let lowerQuartile = 0;
              if (seriesBidRange) {
                upperQuartile = Math.max(seriesBidRange.data[1], seriesBidRange.data[2]);
                lowerQuartile = Math.min(seriesBidRange.data[1], seriesBidRange.data[2]);
              }
              const bidWinRate = 0.8 * (1 + (Math.random() * 0.1 - 0.05));
              return [
                `<b class="text-muted">${date}</b><br/>`,
                ...(seriesBidRange != null && seriesAvg != null
                  ? [
                      `${seriesBidRange.marker} ${seriesBidRange.seriesName}`,
                      '<ul>',
                      `<li><span class="chart-popup-series">Upper quartile:</span><span class="chart-popup-value">${formatNumber(upperQuartile, '$0,0.00')}</span><br/></li>`,
                      `<li><span class="chart-popup-series">Weighted average:</span><span class="chart-popup-value">${formatNumber(seriesAvg.data, '$0,0.00')}</span><br/></li>`,
                      `<li><span class="chart-popup-series">Lower quartile:</span><span class="chart-popup-value">${formatNumber(lowerQuartile, '$0,0.00')}</span><br/></li>`,
                      '</ul>',
                    ]
                  : []),
                seriesMax == null
                  ? ''
                  : `<span class="chart-popup-series">${seriesMax.marker} ${seriesMax.seriesName}:</span>
                    <span class="chart-popup-value">${formatNumber(seriesMax.data, '$0,0.00')}</span><br/>`,
                seriesAvg == null
                  ? ''
                  : `<span class="chart-popup-series">${seriesAvg.marker} ${seriesAvg.seriesName}:</span>
                    <span class="chart-popup-value">${formatNumber(seriesAvg.data, '$0,0.00')}</span><br/>`,
                seriesMin == null
                  ? ''
                  : `<span class="chart-popup-series">${seriesMin.marker} ${seriesMin.seriesName}:</span>
                  <span class="chart-popup-value">${formatNumber(seriesMin.data, '$0,0.00')}</span><br/>`,
                seriesVolume == null
                  ? ''
                  : `<span class="chart-popup-series">${seriesVolume.marker} ${seriesVolume.seriesName}:</span>
                    <span class="chart-popup-value">${formatNumber(seriesVolume.data[1], '0,0')}</span><br/>`,
                `<span class="chart-popup-series">Bid win rate:</span><span class="chart-popup-value">${formatNumber(bidWinRate, '0.0%')}</span><br>`,
              ].join('');
            },
          },
          axisPointer: {
            link: { xAxisIndex: 'all' },
            label: {
              backgroundColor: '#777',
            },
          },
          toolbox: {
            show: false,
            feature: {
              dataZoom: {
                yAxisIndex: false,
              },
              brush: {
                type: ['lineX', 'clear'],
              },
            },
          },
          brush: {
            xAxisIndex: 'all',
            brushLink: 'all',
            outOfBrush: {
              colorAlpha: 0.1,
            },
          },
          visualMap: {
            show: false,
            seriesIndex: 5,
            dimension: 2,
            pieces: [
              {
                value: 1,
                color: vGradient,
              },
              {
                value: -1,
                color: vGradient,
              },
            ],
          },
          grid: [
            {
              left: '10%',
              right: '8%',
              height: '50%',
            },
            {
              left: '10%',
              right: '8%',
              top: '63%',
              height: '16%',
            },
          ],
          xAxis: [
            {
              cellType: CellType.INTEGER,
              type: 'category',
              data: data.categoryData,
              scale: true,
              boundaryGap: false,
              axisLine: { onZero: false },
              splitLine: { show: false },
              splitNumber: 20,
              min: 'dataMin',
              max: 'dataMax',
              axisPointer: {
                z: 100,
              },
            },
            {
              cellType: CellType.INTEGER,
              type: 'category',
              gridIndex: 1,
              data: data.categoryData,
              scale: true,
              boundaryGap: false,
              axisLine: { onZero: false },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              splitNumber: 20,
              min: 'dataMin',
              max: 'dataMax',
            },
          ],
          yAxis: [
            {
              scale: true,
              splitArea: {
                show: true,
              },
            },
            {
              scale: true,
              gridIndex: 1,
              splitNumber: 2,
              axisLabel: { show: false },
              axisLine: { show: false },
              axisTick: { show: false },
              splitLine: { show: false },
            },
          ],
          dataZoom: [
            {
              type: 'inside',
              xAxisIndex: [0, 1],
              start: 94,
              end: 100,
            },
            {
              show: true,
              xAxisIndex: [0, 1],
              type: 'slider',
              top: '85%',
              start: 94,
              end: 100,
            },
          ],
          series: [
            {
              cellType: CellType.INTEGER,
              name: 'Bid range',
              type: 'candlestick',
              data: data.values,
              itemStyle: {
                color: vGradient,
                color0: vGradient,
                borderColor: '#9bb0cc99',
                borderColor0: '#9bb0cc99',
                // shadowColor: '#00000033',
                // shadowBlur: 2,
                // shadowOffsetY: 2,
              },
              markLine: {
                symbol: 'none',
                lineStyle: {
                  color: '#555',
                  shadowColor: '#00000033',
                  shadowBlur: 2,
                  shadowOffsetY: 2,
                },
                data: [
                  {
                    label: {
                      position: 'end',
                      show: true,
                      formatter: 'Today',
                      fontWeight: 'bold',
                    },
                    xAxis: DateTime.local().startOf('day').toLocaleString(DateTime.DATETIME_MED),
                  },
                  {
                    label: {
                      position: 'end',
                      show: true,
                      formatter: 'Campaign start',
                      fontWeight: 'bold',
                    },
                    xAxis: DateTime.fromISO(this.lineItem.startDate)
                      .startOf('day')
                      .toLocaleString(DateTime.DATETIME_MED),
                  },
                ],
              },
            },
            {
              cellType: CellType.INTEGER,
              name: 'Max',
              type: 'line',
              data: data.values.map((x) => x[3]),
              smooth: true,
              lineStyle: {
                opacity: 1,
                color: '#ff0083',
                width: 3,
                shadowColor: '#00000033',
                shadowBlur: 3,
                shadowOffsetY: 3,
              },
            },
            {
              cellType: CellType.INTEGER,
              name: 'Avg',
              type: 'line',
              data: data.values.map(
                (x) => Math.round(100 * (Math.min(x[0], x[1]) + Math.abs(Math.random() * (x[1] - x[0])))) / 100,
              ),
              smooth: true,
              lineStyle: {
                opacity: 1,
                color: '#0F78FF',
                width: 3,
                shadowColor: '#00000033',
                shadowBlur: 3,
                shadowOffsetY: 3,
              },
            },
            {
              cellType: CellType.INTEGER,
              name: 'Min',
              type: 'line',
              data: data.values.map((x) => x[2]),
              smooth: true,
              lineStyle: {
                opacity: 1,
                color: '#00e074',
                width: 3,
                shadowColor: '#00000033',
                shadowBlur: 3,
                shadowOffsetY: 3,
              },
            },
            // {
            //   name: 'MA30',
            //   type: 'line',
            //   data: this.calculateMA(30, data),
            //   smooth: true,
            //   lineStyle: {
            //     opacity: 0.5,
            //   },
            // },
            {
              cellType: CellType.INTEGER,
              name: 'Bids',
              type: 'bar',
              color: vGradient2,
              itemStyle: {
                opacity: 0.8,
                // borderColor: '#fff',
                shadowColor: '#00000033',
                shadowBlur: 3,
                shadowOffsetY: 3,
              },
              xAxisIndex: 1,
              yAxisIndex: 1,
              data: data.volumes,
            },
          ],
        };
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  .historical-bid-chart {
    width: 100%;
    height: 55rem;

    .echarts {
      width: 100% !important;
      height: 55rem !important;
      margin: 0 !important;
    }

    ul {
      margin: 0;
      padding-left: 3rem;
      list-style: square;

      .chart-popup-series {
        width: 15rem;
      }
    }

    .chart-popup-series {
      width: 18rem;
      display: inline-block;
      font-family: $font-family;
    }

    .chart-popup-value {
      width: 6rem;
      text-align: right;
      display: inline-block;
      font-weight: 700;
      /*font-family: $monospace-font-family;*/
    }
  }
</style>
